import Script from 'next/script'
import { ForwardedRef, forwardRef, useImperativeHandle } from 'react'

import { useTurnstile } from './_hooks'
import { TurnstileWidgetHandle, TurnstileWidgetProps } from './type'

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

// Cloudflare Turnstile (Managed) widget. next/script loads the Cloudflare API
// once and dedups by src; onReady fires after load and on every mount, so the
// widget is (re)rendered explicitly there. The token is surfaced via onSuccess.
// The parent gets a reset() handle through a ref to request a fresh token after
// a login failure (tokens are single-use and expire after ~300s).
function TurnstileWidgetBase(
  {
    onSuccess,
    onError,
    onExpire,
    onScriptLoadError,
    className,
  }: TurnstileWidgetProps,
  ref: ForwardedRef<TurnstileWidgetHandle>
) {
  const { containerRef, renderWidget, reset } = useTurnstile({
    onSuccess,
    onError,
    onExpire,
  })

  useImperativeHandle(ref, () => ({ reset }))

  return (
    <>
      <Script
        src={TURNSTILE_SCRIPT_SRC}
        strategy="afterInteractive"
        onReady={renderWidget}
        onError={onScriptLoadError}
      />
      <div ref={containerRef} className={className} />
    </>
  )
}

export const TurnstileWidget = forwardRef(TurnstileWidgetBase)
